import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check, MapPin, Target, DollarSign, Wrench, Briefcase, Users, TrendingUp, Building2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, API } from '../App';
import { NextFlipLogo } from '../components/Logo';

const STEPS_INVESTOR = [
  { id: 'location', title: 'Your Location', icon: MapPin },
  { id: 'markets', title: 'Target Markets', icon: Target },
  { id: 'deals', title: 'Deal Preferences', icon: Building2 },
  { id: 'budget', title: 'Budget & Volume', icon: DollarSign },
  { id: 'services', title: 'Services Needed', icon: Wrench },
  { id: 'perks', title: 'Perks & Discounts', icon: Sparkles },
];

const STEPS_WHOLESALER = [
  { id: 'location', title: 'Your Location', icon: MapPin },
  { id: 'markets', title: 'Deal Markets', icon: Target },
  { id: 'activity', title: 'Deal Activity', icon: TrendingUp },
  { id: 'buyers', title: 'Typical Buyers', icon: Users },
  { id: 'platform', title: 'Platform Needs', icon: Briefcase },
];

const STEPS_PROVIDER = [
  { id: 'service', title: 'Your Service', icon: Briefcase },
  { id: 'markets', title: 'Markets Served', icon: MapPin },
  { id: 'clients', title: 'Client Goals', icon: Users },
  { id: 'advertising', title: 'Advertising', icon: TrendingUp },
  { id: 'goals', title: 'Your Goals', icon: Target },
];

const DEAL_TYPES = [
  'Single Family', 'Multi-Family', 'Commercial', 'Land', 'Mobile Homes', 'Mixed Use'
];

const DEALS_PER_YEAR_OPTIONS = [
  '1-5', '6-15', '16-30', '30+'
];

const PRICE_RANGES = [
  'Under $100K', '$100K-$250K', '$250K-$500K', '$500K-$1M', '$1M+'
];

const MATERIALS_SPEND = [
  'Under $10K/year', '$10K-$50K/year', '$50K-$100K/year', '$100K+/year'
];

const MATERIALS_SOURCES = [
  'Big Box Stores', 'Local Suppliers', 'Wholesale Distributors', 'Online Retailers', 'Direct from Manufacturers'
];

const SERVICES_NEEDED = [
  'General Contractor', 'Property Manager', 'Real Estate Attorney', 'Hard Money Lender',
  'Private Lender', 'Home Inspector', 'Appraiser', 'Title Company', 'Insurance Agent', 'CPA/Accountant'
];

const PROPERTY_TYPES = ['SFR', 'Duplex', 'Triplex', 'Quadplex', 'Small Multi (5-10)', 'Larger Multi (11+)', 'Commercial', 'Land'];

const DEAL_SPEED = ['Quick Closings (7-14 days)', 'Standard (30 days)', 'Flexible'];

const BUYER_TYPES = ['Buy & Hold Investors', 'Fix & Flip Investors', 'Landlords', 'Owner Occupants', 'Institutional Buyers'];

const WHOLESALER_PLATFORM_NEEDS = [
  'Find more qualified buyers', 'Get more deal leads', 'Build credibility', 'Network with other wholesalers', 'Access to funding'
];

const DEALS_PER_MONTH = ['1-2', '3-5', '6-10', '10+'];

const PROVIDER_SERVICES = [
  'General Contractor', 'Property Manager', 'Real Estate Agent/Broker', 'Hard Money Lender', 'Private Lender',
  'Home Inspector', 'Appraiser', 'Title Company', 'Insurance Agent', 'Real Estate Attorney', 'CPA/Accountant', 'Other'
];

const CLIENT_SIZES = ['Individual Investors', 'Small Investment Groups', 'Large Investment Firms', 'Institutional Clients'];

const AD_CHANNELS = ['Google Ads', 'Facebook/Instagram', 'LinkedIn', 'Direct Mail', 'Referrals Only', 'None Currently'];

const AD_SPEND = ['$0 (Referrals Only)', 'Under $500/mo', '$500-$2K/mo', '$2K-$5K/mo', '$5K+/mo'];

const PROVIDER_GOALS = [
  'Get more clients', 'Build brand awareness', 'Network with investors', 'Expand to new markets', 'Increase revenue'
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [existingData, setExistingData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Investor data
  const [investorData, setInvestorData] = useState({
    based_city: '',
    based_state: '',
    based_zip: '',
    target_markets: [],
    deal_types: [],
    deals_per_year: '',
    purchase_price_range: '',
    materials_spend: '',
    materials_sources: [],
    services_needed: [],
    wants_discounts: null,
  });

  // Wholesaler data
  const [wholesalerData, setWholesalerData] = useState({
    based_city: '',
    based_state: '',
    deal_markets: [],
    deals_per_month: '',
    property_types: [],
    price_range: '',
    deal_speed: '',
    typical_buyers: [],
    platform_needs: [],
    interested_premium: null,
  });

  // Provider data
  const [providerData, setProviderData] = useState({
    service_type: '',
    markets_served: [],
    clients_per_month: '',
    client_size_preference: [],
    advertising_channels: [],
    advertising_spend: '',
    primary_goal: '',
    interested_premium: null,
  });

  // New market input
  const [newMarket, setNewMarket] = useState({ type: 'city', value: '' });

  const role = user?.role || 'investor';
  // Realtors use the provider onboarding flow since they're connecting with investor clients
  const effectiveRole = role === 'realtor' ? 'provider' : role;
  const STEPS = effectiveRole === 'investor' ? STEPS_INVESTOR : effectiveRole === 'wholesaler' ? STEPS_WHOLESALER : STEPS_PROVIDER;

  useEffect(() => {
    fetchExistingOnboarding();
  }, []);

  const fetchExistingOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/onboarding`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setExistingData(response.data);
        // Pre-fill data based on role
        if (response.data.investor && effectiveRole === 'investor') {
          setInvestorData(prev => ({ ...prev, ...response.data.investor }));
        }
        if (response.data.wholesaler && effectiveRole === 'wholesaler') {
          setWholesalerData(prev => ({ ...prev, ...response.data.wholesaler }));
        }
        if (response.data.provider && (effectiveRole === 'provider' || role === 'realtor')) {
          setProviderData(prev => ({ ...prev, ...response.data.provider }));
        }
      }
    } catch (error) {
      // No existing onboarding, that's fine
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addMarket = (dataKey, setData) => {
    if (newMarket.value.trim()) {
      setData(prev => ({
        ...prev,
        [dataKey]: [...(prev[dataKey] || []), { type: newMarket.type, value: newMarket.value.trim() }]
      }));
      setNewMarket({ type: newMarket.type, value: '' });
    }
  };

  const removeMarket = (dataKey, setData, index) => {
    setData(prev => ({
      ...prev,
      [dataKey]: prev[dataKey].filter((_, i) => i !== index)
    }));
  };

  const toggleArrayItem = (arr, item, setData, field) => {
    setData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        role: role,
        investor: effectiveRole === 'investor' ? investorData : null,
        wholesaler: effectiveRole === 'wholesaler' ? wholesalerData : null,
        provider: (effectiveRole === 'provider' || role === 'realtor') ? providerData : null,
      };

      await axios.post(`${API}/onboarding`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser({ onboarding_completed: true });
      toast.success('Onboarding complete! Welcome to NextFlip.');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error('Failed to save onboarding data');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5DADE2]" />
      </div>
    );
  }

  // Render step content based on role
  const renderStepContent = () => {
    const step = STEPS[currentStep];

    // INVESTOR STEPS
    if (effectiveRole === 'investor') {
      switch (step.id) {
        case 'location':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">Where are you based?</Label>
                <p className="text-sm text-slate-500 mb-4">We'll use this to personalize your experience</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">City</Label>
                    <Input
                      placeholder="e.g., Austin"
                      value={investorData.based_city}
                      onChange={(e) => setInvestorData(prev => ({ ...prev, based_city: e.target.value }))}
                      className="mt-1"
                      data-testid="onboarding-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">State</Label>
                    <Input
                      placeholder="e.g., TX"
                      value={investorData.based_state}
                      onChange={(e) => setInvestorData(prev => ({ ...prev, based_state: e.target.value }))}
                      className="mt-1"
                      data-testid="onboarding-state-input"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-sm text-slate-600">ZIP Code (optional)</Label>
                  <Input
                    placeholder="e.g., 78701"
                    value={investorData.based_zip}
                    onChange={(e) => setInvestorData(prev => ({ ...prev, based_zip: e.target.value }))}
                    className="mt-1 max-w-[200px]"
                    data-testid="onboarding-zip-input"
                  />
                </div>
              </div>
            </div>
          );

        case 'markets':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">What markets are you interested in?</Label>
                <p className="text-sm text-slate-500 mb-4">Add cities, states, or ZIP codes where you're looking to invest</p>
                
                <div className="flex gap-2 mb-4">
                  <select
                    value={newMarket.type}
                    onChange={(e) => setNewMarket(prev => ({ ...prev, type: e.target.value }))}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    data-testid="market-type-select"
                  >
                    <option value="city">City</option>
                    <option value="state">State</option>
                    <option value="zip">ZIP Code</option>
                  </select>
                  <Input
                    placeholder={newMarket.type === 'city' ? 'Enter city name' : newMarket.type === 'state' ? 'Enter state' : 'Enter ZIP'}
                    value={newMarket.value}
                    onChange={(e) => setNewMarket(prev => ({ ...prev, value: e.target.value }))}
                    className="flex-1"
                    data-testid="market-value-input"
                  />
                  <Button
                    type="button"
                    onClick={() => addMarket('target_markets', setInvestorData)}
                    variant="outline"
                    className="rounded-full"
                    data-testid="add-market-btn"
                  >
                    Add
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {investorData.target_markets.map((market, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-sky-50 text-sky-700 rounded-full text-sm"
                    >
                      <span className="text-xs text-sky-500 uppercase">{market.type}:</span>
                      {market.value}
                      <button
                        onClick={() => removeMarket('target_markets', setInvestorData, idx)}
                        className="ml-1 hover:text-red-500"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {investorData.target_markets.length === 0 && (
                    <p className="text-slate-400 text-sm">No markets added yet</p>
                  )}
                </div>
              </div>
            </div>
          );

        case 'deals':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">What types of deals interest you?</Label>
                <p className="text-sm text-slate-500 mb-4">Select all that apply</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {DEAL_TYPES.map((type) => (
                    <label
                      key={type}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        investorData.deal_types.includes(type)
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`deal-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Checkbox
                        checked={investorData.deal_types.includes(type)}
                        onCheckedChange={() => toggleArrayItem(investorData.deal_types, type, setInvestorData, 'deal_types')}
                      />
                      <span className="text-sm text-slate-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );

        case 'budget':
          return (
            <div className="space-y-8">
              <div>
                <Label className="text-slate-700 font-medium">How many deals do you typically do per year?</Label>
                <RadioGroup
                  value={investorData.deals_per_year}
                  onValueChange={(v) => setInvestorData(prev => ({ ...prev, deals_per_year: v }))}
                  className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4"
                >
                  {DEALS_PER_YEAR_OPTIONS.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        investorData.deals_per_year === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt} deals</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">What's your typical purchase price range?</Label>
                <RadioGroup
                  value={investorData.purchase_price_range}
                  onValueChange={(v) => setInvestorData(prev => ({ ...prev, purchase_price_range: v }))}
                  className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4"
                >
                  {PRICE_RANGES.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        investorData.purchase_price_range === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          );

        case 'services':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">What services do you need most?</Label>
                <p className="text-sm text-slate-500 mb-4">We'll recommend providers based on your selection</p>
                <div className="grid grid-cols-2 gap-3">
                  {SERVICES_NEEDED.map((service) => (
                    <label
                      key={service}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        investorData.services_needed.includes(service)
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={investorData.services_needed.includes(service)}
                        onCheckedChange={() => toggleArrayItem(investorData.services_needed, service, setInvestorData, 'services_needed')}
                      />
                      <span className="text-sm text-slate-700">{service}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );

        case 'perks':
          return (
            <div className="space-y-8">
              <div>
                <Label className="text-slate-700 font-medium">How much do you spend on materials per year?</Label>
                <RadioGroup
                  value={investorData.materials_spend}
                  onValueChange={(v) => setInvestorData(prev => ({ ...prev, materials_spend: v }))}
                  className="grid grid-cols-2 gap-3 mt-4"
                >
                  {MATERIALS_SPEND.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        investorData.materials_spend === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Would you like exclusive discounts from our partners?</Label>
                <p className="text-sm text-slate-500 mb-4">Get special pricing on materials, services, and more</p>
                <RadioGroup
                  value={investorData.wants_discounts === true ? 'yes' : investorData.wants_discounts === false ? 'no' : ''}
                  onValueChange={(v) => setInvestorData(prev => ({ ...prev, wants_discounts: v === 'yes' }))}
                  className="flex gap-4"
                >
                  <label className={`flex items-center gap-2 px-6 py-3 rounded-full border cursor-pointer transition-all ${
                    investorData.wants_discounts === true ? 'border-[#5DADE2] bg-sky-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="yes" className="sr-only" />
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <span>Yes, I want discounts!</span>
                  </label>
                  <label className={`flex items-center gap-2 px-6 py-3 rounded-full border cursor-pointer transition-all ${
                    investorData.wants_discounts === false ? 'border-[#5DADE2] bg-sky-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="no" className="sr-only" />
                    <span>Not right now</span>
                  </label>
                </RadioGroup>
              </div>
            </div>
          );

        default:
          return null;
      }
    }

    // WHOLESALER STEPS
    if (effectiveRole === 'wholesaler') {
      switch (step.id) {
        case 'location':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">Where are you based?</Label>
                <p className="text-sm text-slate-500 mb-4">Your home base for wholesaling operations</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">City</Label>
                    <Input
                      placeholder="e.g., Dallas"
                      value={wholesalerData.based_city}
                      onChange={(e) => setWholesalerData(prev => ({ ...prev, based_city: e.target.value }))}
                      className="mt-1"
                      data-testid="onboarding-ws-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">State</Label>
                    <Input
                      placeholder="e.g., TX"
                      value={wholesalerData.based_state}
                      onChange={(e) => setWholesalerData(prev => ({ ...prev, based_state: e.target.value }))}
                      className="mt-1"
                      data-testid="onboarding-ws-state-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          );

        case 'markets':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">What markets do you source deals in?</Label>
                <p className="text-sm text-slate-500 mb-4">Add the areas where you find and wholesale properties</p>
                
                <div className="flex gap-2 mb-4">
                  <select
                    value={newMarket.type}
                    onChange={(e) => setNewMarket(prev => ({ ...prev, type: e.target.value }))}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="city">City</option>
                    <option value="state">State</option>
                    <option value="zip">ZIP Code</option>
                  </select>
                  <Input
                    placeholder={newMarket.type === 'city' ? 'Enter city name' : newMarket.type === 'state' ? 'Enter state' : 'Enter ZIP'}
                    value={newMarket.value}
                    onChange={(e) => setNewMarket(prev => ({ ...prev, value: e.target.value }))}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => addMarket('deal_markets', setWholesalerData)}
                    variant="outline"
                    className="rounded-full"
                  >
                    Add
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {wholesalerData.deal_markets.map((market, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-sky-50 text-sky-700 rounded-full text-sm"
                    >
                      <span className="text-xs text-sky-500 uppercase">{market.type}:</span>
                      {market.value}
                      <button
                        onClick={() => removeMarket('deal_markets', setWholesalerData, idx)}
                        className="ml-1 hover:text-red-500"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {wholesalerData.deal_markets.length === 0 && (
                    <p className="text-slate-400 text-sm">No markets added yet</p>
                  )}
                </div>
              </div>
            </div>
          );

        case 'activity':
          return (
            <div className="space-y-8">
              <div>
                <Label className="text-slate-700 font-medium">How many deals do you close per month?</Label>
                <RadioGroup
                  value={wholesalerData.deals_per_month}
                  onValueChange={(v) => setWholesalerData(prev => ({ ...prev, deals_per_month: v }))}
                  className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4"
                >
                  {DEALS_PER_MONTH.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        wholesalerData.deals_per_month === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">What property types do you wholesale?</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  {PROPERTY_TYPES.map((type) => (
                    <label
                      key={type}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        wholesalerData.property_types.includes(type)
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={wholesalerData.property_types.includes(type)}
                        onCheckedChange={() => toggleArrayItem(wholesalerData.property_types, type, setWholesalerData, 'property_types')}
                      />
                      <span className="text-sm text-slate-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Your typical deal price range?</Label>
                <RadioGroup
                  value={wholesalerData.price_range}
                  onValueChange={(v) => setWholesalerData(prev => ({ ...prev, price_range: v }))}
                  className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4"
                >
                  {PRICE_RANGES.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        wholesalerData.price_range === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">How fast do your deals typically close?</Label>
                <RadioGroup
                  value={wholesalerData.deal_speed}
                  onValueChange={(v) => setWholesalerData(prev => ({ ...prev, deal_speed: v }))}
                  className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4"
                >
                  {DEAL_SPEED.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        wholesalerData.deal_speed === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          );

        case 'buyers':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">Who are your typical buyers?</Label>
                <p className="text-sm text-slate-500 mb-4">Select all that apply</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {BUYER_TYPES.map((type) => (
                    <label
                      key={type}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        wholesalerData.typical_buyers.includes(type)
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={wholesalerData.typical_buyers.includes(type)}
                        onCheckedChange={() => toggleArrayItem(wholesalerData.typical_buyers, type, setWholesalerData, 'typical_buyers')}
                      />
                      <span className="text-sm text-slate-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );

        case 'platform':
          return (
            <div className="space-y-8">
              <div>
                <Label className="text-slate-700 font-medium">What do you want from NextFlip?</Label>
                <p className="text-sm text-slate-500 mb-4">Help us serve you better</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {WHOLESALER_PLATFORM_NEEDS.map((need) => (
                    <label
                      key={need}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        wholesalerData.platform_needs.includes(need)
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={wholesalerData.platform_needs.includes(need)}
                        onCheckedChange={() => toggleArrayItem(wholesalerData.platform_needs, need, setWholesalerData, 'platform_needs')}
                      />
                      <span className="text-sm text-slate-700">{need}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Interested in premium features?</Label>
                <p className="text-sm text-slate-500 mb-4">Featured listings, analytics, priority placement</p>
                <RadioGroup
                  value={wholesalerData.interested_premium === true ? 'yes' : wholesalerData.interested_premium === false ? 'no' : ''}
                  onValueChange={(v) => setWholesalerData(prev => ({ ...prev, interested_premium: v === 'yes' }))}
                  className="flex gap-4"
                >
                  <label className={`flex items-center gap-2 px-6 py-3 rounded-full border cursor-pointer transition-all ${
                    wholesalerData.interested_premium === true ? 'border-[#5DADE2] bg-sky-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="yes" className="sr-only" />
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <span>Yes, tell me more</span>
                  </label>
                  <label className={`flex items-center gap-2 px-6 py-3 rounded-full border cursor-pointer transition-all ${
                    wholesalerData.interested_premium === false ? 'border-[#5DADE2] bg-sky-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="no" className="sr-only" />
                    <span>Maybe later</span>
                  </label>
                </RadioGroup>
              </div>
            </div>
          );

        default:
          return null;
      }
    }

    // PROVIDER STEPS (also used for Realtors)
    if (effectiveRole === 'provider') {
      switch (step.id) {
        case 'service':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">What service do you provide?</Label>
                <p className="text-sm text-slate-500 mb-4">Select your primary service category</p>
                <RadioGroup
                  value={providerData.service_type}
                  onValueChange={(v) => setProviderData(prev => ({ ...prev, service_type: v }))}
                  className="grid grid-cols-2 md:grid-cols-3 gap-3"
                >
                  {PROVIDER_SERVICES.map((service) => (
                    <label
                      key={service}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        providerData.service_type === service
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={service} className="sr-only" />
                      <span className="text-sm text-slate-700">{service}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          );

        case 'markets':
          return (
            <div className="space-y-6">
              <div>
                <Label className="text-slate-700 font-medium">What markets do you serve?</Label>
                <p className="text-sm text-slate-500 mb-4">Add the areas where you offer your services</p>
                
                <div className="flex gap-2 mb-4">
                  <select
                    value={newMarket.type}
                    onChange={(e) => setNewMarket(prev => ({ ...prev, type: e.target.value }))}
                    className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  >
                    <option value="city">City</option>
                    <option value="state">State</option>
                    <option value="region">Region</option>
                  </select>
                  <Input
                    placeholder="Enter location"
                    value={newMarket.value}
                    onChange={(e) => setNewMarket(prev => ({ ...prev, value: e.target.value }))}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={() => addMarket('markets_served', setProviderData)}
                    variant="outline"
                    className="rounded-full"
                  >
                    Add
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {providerData.markets_served.map((market, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-sky-50 text-sky-700 rounded-full text-sm"
                    >
                      <span className="text-xs text-sky-500 uppercase">{market.type}:</span>
                      {market.value}
                      <button
                        onClick={() => removeMarket('markets_served', setProviderData, idx)}
                        className="ml-1 hover:text-red-500"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {providerData.markets_served.length === 0 && (
                    <p className="text-slate-400 text-sm">No markets added yet</p>
                  )}
                </div>
              </div>
            </div>
          );

        case 'clients':
          return (
            <div className="space-y-8">
              <div>
                <Label className="text-slate-700 font-medium">How many new clients do you want per month?</Label>
                <RadioGroup
                  value={providerData.clients_per_month}
                  onValueChange={(v) => setProviderData(prev => ({ ...prev, clients_per_month: v }))}
                  className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4"
                >
                  {['1-5', '6-15', '16-30', '30+'].map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        providerData.clients_per_month === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">What type of clients do you prefer?</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  {CLIENT_SIZES.map((size) => (
                    <label
                      key={size}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        providerData.client_size_preference.includes(size)
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={providerData.client_size_preference.includes(size)}
                        onCheckedChange={() => toggleArrayItem(providerData.client_size_preference, size, setProviderData, 'client_size_preference')}
                      />
                      <span className="text-sm text-slate-700">{size}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );

        case 'advertising':
          return (
            <div className="space-y-8">
              <div>
                <Label className="text-slate-700 font-medium">How do you currently get clients?</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                  {AD_CHANNELS.map((channel) => (
                    <label
                      key={channel}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        providerData.advertising_channels.includes(channel)
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <Checkbox
                        checked={providerData.advertising_channels.includes(channel)}
                        onCheckedChange={() => toggleArrayItem(providerData.advertising_channels, channel, setProviderData, 'advertising_channels')}
                      />
                      <span className="text-sm text-slate-700">{channel}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">What's your current advertising budget?</Label>
                <RadioGroup
                  value={providerData.advertising_spend}
                  onValueChange={(v) => setProviderData(prev => ({ ...prev, advertising_spend: v }))}
                  className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4"
                >
                  {AD_SPEND.map((opt) => (
                    <label
                      key={opt}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        providerData.advertising_spend === opt
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={opt} className="sr-only" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          );

        case 'goals':
          return (
            <div className="space-y-8">
              <div>
                <Label className="text-slate-700 font-medium">What's your primary goal on NextFlip?</Label>
                <RadioGroup
                  value={providerData.primary_goal}
                  onValueChange={(v) => setProviderData(prev => ({ ...prev, primary_goal: v }))}
                  className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4"
                >
                  {PROVIDER_GOALS.map((goal) => (
                    <label
                      key={goal}
                      className={`flex items-center justify-center p-3 rounded-lg border cursor-pointer transition-all ${
                        providerData.primary_goal === goal
                          ? 'border-[#5DADE2] bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <RadioGroupItem value={goal} className="sr-only" />
                      <span className="text-sm text-slate-700">{goal}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className="text-slate-700 font-medium">Interested in premium features?</Label>
                <p className="text-sm text-slate-500 mb-4">Featured listing, priority placement, analytics</p>
                <RadioGroup
                  value={providerData.interested_premium === true ? 'yes' : providerData.interested_premium === false ? 'no' : ''}
                  onValueChange={(v) => setProviderData(prev => ({ ...prev, interested_premium: v === 'yes' }))}
                  className="flex gap-4"
                >
                  <label className={`flex items-center gap-2 px-6 py-3 rounded-full border cursor-pointer transition-all ${
                    providerData.interested_premium === true ? 'border-[#5DADE2] bg-sky-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="yes" className="sr-only" />
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <span>Yes, tell me more</span>
                  </label>
                  <label className={`flex items-center gap-2 px-6 py-3 rounded-full border cursor-pointer transition-all ${
                    providerData.interested_premium === false ? 'border-[#5DADE2] bg-sky-50' : 'border-slate-200'
                  }`}>
                    <RadioGroupItem value="no" className="sr-only" />
                    <span>Maybe later</span>
                  </label>
                </RadioGroup>
              </div>
            </div>
          );

        default:
          return null;
      }
    }

    return null;
  };

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <NextFlipLogo size="small" />
          <button
            onClick={handleSkip}
            className="text-sm text-slate-500 hover:text-slate-700"
            data-testid="skip-onboarding-btn"
          >
            Skip for now
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">
              Step {currentStep + 1} of {STEPS.length}
            </span>
            <span className="text-sm text-slate-500">{STEPS[currentStep].title}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#5DADE2] to-[#4A9BD1] transition-all duration-300"
              style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Step Indicators */}
      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isComplete = idx < currentStep;
            const isCurrent = idx === currentStep;

            return (
              <div
                key={step.id}
                className={`flex items-center gap-2 ${idx < STEPS.length - 1 ? 'flex-1' : ''}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isComplete
                      ? 'bg-green-500 text-white'
                      : isCurrent
                      ? 'bg-[#5DADE2] text-white'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {isComplete ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 rounded ${
                      isComplete ? 'bg-green-500' : 'bg-slate-200'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            {React.createElement(STEPS[currentStep].icon, { className: 'h-6 w-6 text-[#5DADE2]' })}
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              {STEPS[currentStep].title}
            </h2>
          </div>

          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="rounded-full"
            data-testid="onboarding-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          {isLastStep ? (
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full px-8"
              data-testid="onboarding-submit-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  Complete Setup
                  <Check className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full px-8"
              data-testid="onboarding-next-btn"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
